import { useMemo } from 'react';
import {
  ContentAccessUser,
  ContentWorkflow,
  UserProfile,
} from '@amzn/awscat-pattern-library-service-model';
import { DateTime } from 'luxon';

import { ToolbarAction, disableToolbarActions } from '../../services/ToolbarActionService';
import { ActionPanelState } from '../v2-content-action-panel/ActionPanelTypes';
import { useActionPanelState } from '../v2-hooks/useActionPanelState';
import { useBarRaiserEvaluationTab } from '../v2-hooks/useBarRaiserEvaluationTab';
import { useViewContentLoad } from '../v2-hooks/useViewContentLoad';
import { useEntityConverter } from '../v2-hooks/useEntityConverter';
import { useToolbarActionService } from '../../contexts/ToolbarActionServiceContext';
import { useContent } from '../v2-hooks/useContent';
import { useCurrentUser } from '../../contexts/UserContext';

const HISTORY_ACTION: ToolbarAction = {
  id: 'history',
  icon: 'history',
  tooltip: 'History',
  sortOrder: 2,
};

// Draft view surfaces the full workflow toolbar.
const DRAFT_TOOLBAR_ACTIONS: readonly ToolbarAction[] = [
  {
    id: 'publication-state',
    icon: 'status-in-progress',
    tooltip: 'Publication state',
    sortOrder: 0,
  },
  { id: 'comments', icon: 'contact', tooltip: 'Comments', sortOrder: 1 },
  HISTORY_ACTION,
  { id: 'about-stage', icon: 'status-info', tooltip: 'About this stage', sortOrder: 3 },
];

// We show only the history for a published version
const PUBLICATION_TOOLBAR_ACTIONS: readonly ToolbarAction[] = [HISTORY_ACTION];
const NO_TOOLBAR_ACTIONS: readonly ToolbarAction[] = [];

/** Content is still being fetched. */
export interface ContentViewPageLoadingViewModel {
  readonly status: 'loading';
  readonly activePanel: ActionPanelState;
  readonly closePanel: () => void;
}

/** Load failed. `errorMessage` is ready to display to the user. */
export interface ContentViewPageErrorViewModel {
  readonly status: 'error';
  readonly errorMessage: string;
  readonly activePanel: ActionPanelState;
  readonly closePanel: () => void;
}

/** The content is missing or the viewer is not authorized to see it. */
export interface ContentViewPageNotFoundViewModel {
  readonly status: 'notFound';
  readonly activePanel: ActionPanelState;
  readonly closePanel: () => void;
}

/** Load succeeded. All fields are present. */
export interface ContentViewPageLoadedViewModel {
  readonly status: 'loaded';
  /** Display title with APGL entities already replaced. */
  readonly title: string;
  readonly workflow: ContentWorkflow | undefined;
  readonly createdAt: DateTime | undefined;
  readonly author: UserProfile;
  readonly coAuthors: readonly ContentAccessUser[];
  readonly activePanel: ActionPanelState;
  readonly closePanel: () => void;
}

export type ContentViewPageViewModel =
  | ContentViewPageLoadingViewModel
  | ContentViewPageErrorViewModel
  | ContentViewPageNotFoundViewModel
  | ContentViewPageLoadedViewModel;

/**
 * Opens `contentId` in edit-draft mode on the {@link ContentEditorService},
 * loads the inline comments for the draft workflow, and binds the page to
 * the service's loaded document. Drives the right-side action panel state
 * and registers the header toolbar actions, including the bar raiser
 * evaluation tab during publisher review. On unmount, both services are
 * closed and the toolbar actions are unregistered.
 */
export function useContentViewPageViewModel(): ContentViewPageViewModel {
  const convertEntitiesInText = useEntityConverter();
  const { loadedDocumentHandle, isLoading, errorMessage, isNotFound, isPublicationView } =
    useViewContentLoad();
  const toolbarActionService = useToolbarActionService();
  const content = useContent();
  const { canSeeActivityForContent } = useCurrentUser();

  // The bar raiser evaluation tab only appears during the publisher review
  // stage; the hook also prefetches the questionnaire when it applies.
  const workflow =
    loadedDocumentHandle?.document.kind === 'draft'
      ? loadedDocumentHandle.document.workflow
      : undefined;
  const barRaiserEvaluationActions = useBarRaiserEvaluationTab(workflow);

  // A published version omits the draft-only toolbar surfaces (publication
  // state, comments, about-stage, bar raiser); only history applies, and only
  // for the content's authors and admins, publishers, tech writers, and bar
  // raisers. Until the content has loaded — while loading, or when it is
  // missing or forbidden — the actions act on content that isn't there, so
  // register them disabled. This also keeps the default panel closed so it
  // doesn't flash open during loading only to close on a 404.
  const loadedDocument = loadedDocumentHandle?.document;
  // When the loaded document or the user changes, re-evaluate activity visibility.
  const canSeeActivity = useMemo(
    () =>
      loadedDocument !== undefined &&
      canSeeActivityForContent(
        loadedDocument.author.userId,
        loadedDocument.coAuthors.map((coAuthor) => coAuthor.userId),
      ),
    [canSeeActivityForContent, loadedDocument],
  );
  const toolbarActions = useMemo<readonly ToolbarAction[]>(() => {
    const publicationActions = canSeeActivity ? PUBLICATION_TOOLBAR_ACTIONS : NO_TOOLBAR_ACTIONS;
    const actions = isPublicationView
      ? publicationActions
      : [...DRAFT_TOOLBAR_ACTIONS, ...barRaiserEvaluationActions];
    return isLoading || isNotFound ? disableToolbarActions(actions) : actions;
  }, [isPublicationView, canSeeActivity, barRaiserEvaluationActions, isLoading, isNotFound]);

  // Register toolbar
  const actionPanelState = useActionPanelState();
  actionPanelState.registerToolbarActions(toolbarActions);

  const { activePanel } = actionPanelState;
  const closePanel = () => (toolbarActionService.activePanel = null);

  // Narrow into the discriminated view model.
  // Not-found and error win over loading; loaded only when the handle and the content is set.
  if (isNotFound) return { status: 'notFound', activePanel, closePanel };

  if (errorMessage !== undefined) return { status: 'error', errorMessage, activePanel, closePanel };

  if (isLoading || !loadedDocumentHandle || !content)
    return { status: 'loading', activePanel, closePanel };

  const { document } = loadedDocumentHandle;
  return {
    status: 'loaded',
    title: convertEntitiesInText(document.content.contentMetadata.contentName ?? ''),
    workflow,
    createdAt: document.kind === 'draft' ? document.createdAt : undefined,
    author: document.author,
    coAuthors: document.coAuthors,
    activePanel,
    closePanel,
  };
}
